/// <amd-module name="TFS/Token/PatTokenPage/Components/NewTokenList" />
import { PanelActionCreator, PanelOperationType } from 'Actions/PanelActionCreator';
import { TokenActionCreator } from 'Actions/TokenActionCreator';
import { NoTokenDisplay } from 'Components/NoTokenDisplay';
import { AccountNameConsumer, IAccountNameContextValue } from 'Context/AccountNameContext';
import { IPatPageContextValue, PatPageConsumer } from 'Context/PatPageContext';
import { ITokenContextValue, TokenConsumer } from 'Context/TokenContext';
import { DisplayFilterOptions, SessionToken, SortByOptions } from 'Contracts/Token';
import { TokenConfig } from 'Models/TokenConfig';
import { Token } from 'Models/Token';
import { FontSizes, FontWeights, ITheme, getTheme, mergeStyleSets, mergeStyles } from "OfficeFabric/Styling";
import * as Resources from 'TFS/Token/PatTokenPage/Resources';
import TokenUtils from 'Utils/TokenUtils';
import { ObservableValue } from "VSS/Core/Observable";
import { memoize } from "VSS/Core/Util/Memoize";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Card } from "VSSUI/Card";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from 'VSSUI/Icon';
import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ColumnSelect, ColumnSorting, ITableColumn, ITableRow, SimpleTableCell, SortOrder, Table } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from 'VSSUI/Utilities/Provider';
import * as React from 'react';

export interface ITokenListProps {
}

export interface ITokenListInternalProps {
    accountNameContext: IAccountNameContextValue;
    tokenContext: ITokenContextValue;
    tokenAction: TokenActionCreator;
    panelAction: PanelActionCreator;
    tokenConfig: TokenConfig;
    pageContext: IVssPageContext;
}

export class TokenListInternal extends React.Component<ITokenListInternalProps, {}> {
    private columns: ITableColumn<Token>[];
    private sortingBehavior: ColumnSorting<Token>;

    constructor(props: ITokenListInternalProps) {
        super(props);
        this.columns = this.getColumns();
        this.sortingBehavior = new ColumnSorting<Token>(this.onSort);
    }

    @memoize
    private static getStyles(theme: ITheme) {
        const { palette } = theme;
        return mergeStyleSets({
            container: {
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
                margin: 48,
            },
            listContainer: {
                display: "flex",
                flexDirection: "column",
                overflow: "auto",
                selectors: {
                    [`& .ms-DetailsRow`]: {
                        alignItems: "center",
                    },
                    [`& .ms-DetailsRow-cell`]: {
                        alignItems: "center",
                    },
                },
            },
            loadingSpinner: {
                padding: 80
            },
            checkboxCell: {
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            },
            listRowInactive: {
                opacity: .6
            },
            tokenName: {
                fontSize: FontSizes.mediumPlus,
                overflow: "hidden",
                textOverflow: "ellipsis"
            },
            tokenScope: {
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: palette.neutralSecondary,
            },
            statusSpan: {
                display: "flex",
                flexDirection: "row",
                lineHeight: FontSizes.xxLarge
            },
            statusIcon: {
                fontSize: FontSizes.xxLarge,
            },
            noTokensForFilter: {
                fontWeight: FontWeights.semilight,
                fontSize: FontSizes.large,
                textAlign: "center",
                maxWidth: 400,
                padding: 20
            },
            loadMoreLink: {
                margin: "14px"
            },
            columnData: {
                marginTop: "8px"
            },
            statusColumn: {
                marginTop: "4px"
            },
            card: {
                marginTop: "15px",
                marginBottom: "15px",
                marginLeft: "32px",
                marginRight: "32px"
            }
        })
    }

    @memoize
    private static getIconColorStyle(theme: ITheme, status: string) {
        const { palette } = theme;
        var color = palette.neutralSecondary;
        if (status === Resources.Expired) {
            color = palette.red;
        }
        else if (status === Resources.Active) {
            color = palette.blue;
        }
        else if (Resources.Revoked) {
            color = palette.neutralSecondary;
        }
        return mergeStyles({
            color,
        })
    }

    public render() {
        const { tokens, selectedFilter, canLoadMoreTokens, selectedToken, listSelection } = this.props.tokenContext;
        const { loadNextPageOfTokens } = this.props.tokenAction;
        const styles = this.getStyles();

        // render spinner if API is still making the call
        if (!tokens) {
            return (
                <Spinner
                    className={styles.loadingSpinner}
                    size={SpinnerSize.large}
                    label={Resources.LoadingTokens}
                />
            )
        }
        else if (tokens.length === 0) {

            // render no token page if zero tokens are returned
            if (selectedFilter.status === DisplayFilterOptions.Active || selectedFilter.status === DisplayFilterOptions.All) {
                return (
                    <NoTokenDisplay />
                )
            } else {
                // render text if there are no tokens for the selected filter
                return (
                    <div className={styles.container}>
                        <div className={styles.noTokensForFilter}>{Resources.NoTokensWithSelectedStatus}</div>
                    </div>
                )
            }
        } else {
            listSelection.unselect(0, tokens.length);

            // Preserve the selected token in this new ListSelection instance
            if (selectedToken) {
                const index = tokens.findIndex(token => token.authorizationId === selectedToken.authorizationId);
                listSelection.select(index);
            }

            return (
                <div className={styles.listContainer} data-is-focusable={true}>
                    <Card
                        className={styles.card}
                        titleProps={{ text: Resources.ManageTokens, ariaLevel: 3 }}
                        headerCommandBarItems={this.getCommandBarButtons()}
                    >
                        <Table
                            ariaLabel={Resources.PersonalAccessTokens}
                            columns={this.columns}
                            itemProvider={new ArrayItemProvider<Token>(tokens)}
                            selection={listSelection}
                            onSelect={this.onSelect}
                            behaviors={[this.sortingBehavior]}
                        />
                    </Card>
                    {canLoadMoreTokens
                        ? <div>
                            <Link
                                className={styles.loadMoreLink}
                                onClick={loadNextPageOfTokens}
                            >
                                {Resources.LoadMoreTokens}
                            </Link>
                        </div>
                        : null}
                </div>
            )
        }
    }

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<Token>) => {
        if (this.props.tokenContext.selectedToken?.authorizationId === tableRow.data.authorizationId) {
            this.props.tokenContext.onSelectedTokenChange(undefined);
        }
        else {
            this.props.tokenContext.onSelectedTokenChange(tableRow.data);
        }
    }

    private onSort = (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
        const { resetTokens } = this.props.tokenAction;

        var selectedSort: SortByOptions;
        selectedSort = SortByOptions.DisplayName;
        switch (columnIndex) {
            case 0:
                selectedSort = SortByOptions.DisplayName;
                break;
            case 3:
                selectedSort = SortByOptions.DisplayDate;
                break;
        }

        this.columns[columnIndex].sortProps = { sortOrder: proposedSortOrder };
        resetTokens(undefined, selectedSort, proposedSortOrder === SortOrder.descending);
    }

    private FormatOrganization = (token: SessionToken): string => {
        const { accountIdToNameMap } = this.props.accountNameContext;
        if (!accountIdToNameMap) {
            return "";
        }
        if (token.targetAccounts) {
            return accountIdToNameMap[token.targetAccounts[0]];
        }
        return Resources.AllAccessibleOrganizations;
    }

    private FormatStatus = (token: SessionToken) => {
        // if the current date > token date, token is expired
        const currentDate = new Date();
        if (currentDate > token.validTo && token.isValid) {
            return Resources.Expired;
        }
        // if isValid is true, token is active
        if (token.isValid) {
            return Resources.Active;
        }
        // if isValid is false, token is revoked
        else {
            return Resources.Revoked;
        }
    }

    private FormatExpirationDate(date: Date) {
        return date.toLocaleDateString();
    }

    private getColumns = (): ITableColumn<Token>[] => {
        const columnSelect = new ColumnSelect();

        return [
            columnSelect,
            {
                id: 'tokenName',
                name: Resources.TokenName,
                renderCell: this.renderTokenNameCell,
                width: new ObservableValue(-40)
            },
            {
                id: 'organization',
                name: Resources.Organization,
                renderCell: this.renderOrganizationCell,
                width: new ObservableValue(-20)
            },
            {
                id: 'status',
                name: Resources.Status,
                renderCell: this.renderStatusCell,
                width: new ObservableValue(-10),
                maxWidth: 200
            },
            {
                id: 'expiresOn',
                name: Resources.ExpiresOn,
                renderCell: this.renderExpiresOnCell,
                width: new ObservableValue(-10),
                maxWidth: 200,
                sortProps: { sortOrder: SortOrder.descending }
            },
        ];
    }

    private renderTokenNameCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Token>,
        dataItem: Token
    ): JSX.Element => {
        const styles = this.getStyles();
        const colorStyle = !dataItem.isValid ? styles.listRowInactive : '';
        const fieldContent = dataItem.displayName;
        const { scopes } = this.props.tokenConfig.tokenPageData;
        const formattedScope = TokenUtils.formatTokenScope(dataItem.scope, scopes);

        return (
            <SimpleTableCell
                key={rowIndex + '-' + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                <div className="flex-column scroll-hidden">
                    <Tooltip overflowOnly={true} text={fieldContent} className={colorStyle}>
                        <div className={css(styles.tokenName, "text-ellipsis")}>{fieldContent}</div>
                    </Tooltip>
                    <Tooltip overflowOnly={true} text={formattedScope} className={colorStyle}>
                        <div className={styles.tokenScope}>{formattedScope}</div>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private renderStatusCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Token>,
        dataItem: Token
    ): JSX.Element => {
        const styles = this.getStyles();
        const colorStyle = !dataItem.isValid ? styles.listRowInactive : '';

        const token = dataItem.toSessionToken();
        const status = this.FormatStatus(token);

        return (
            <SimpleTableCell
                key={rowIndex + '-' + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                <div className={styles.statusSpan}>
                    <div className={css(styles.statusIcon, this.getIconColorStyle(status))} >
                        <Icon iconName="LocationDot" />
                    </div>
                    <div className={colorStyle}>{status}</div>
                </div>
            </SimpleTableCell>
        );
    }

    private renderExpiresOnCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Token>,
        dataItem: Token
    ): JSX.Element => {
        const styles = this.getStyles();
        const colorStyle = !dataItem.isValid ? styles.listRowInactive : '';

        const expirationDate = this.FormatExpirationDate(dataItem.validTo);

        return (
            <SimpleTableCell
                key={rowIndex + '-' + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                <div className={colorStyle}>{expirationDate}</div>
            </SimpleTableCell>
        );
    }

    private renderOrganizationCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<Token>,
        dataItem: Token
    ): JSX.Element => {

        const styles = this.getStyles();
        const colorStyle = !dataItem.isValid ? styles.listRowInactive : '';

        const organization = this.FormatOrganization(dataItem.toSessionToken());

        return (
            <SimpleTableCell
                key={rowIndex + '-' + columnIndex}
                columnIndex={columnIndex}
                tableColumn={tableColumn}
            >
                <Tooltip overflowOnly={true} text={organization} className={colorStyle}>
                    <div className="text-ellipsis">{organization}</div>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private getStyles() {
        const theme = getTheme();
        return TokenListInternal.getStyles(theme);
    }

    private getIconColorStyle(status: string) {
        const theme = getTheme();
        return TokenListInternal.getIconColorStyle(theme, status)
    }

    private getCommandBarButtons(): IHeaderCommandBarItem[] {
        const { selectedToken, selectedFilter } = this.props.tokenContext;
        const { hostPageData } = this.props.tokenConfig;

        let isTokenExpired = false;
        if (this.hideRegenerateButtonToExpiredPersonalAccessTokens()) {
            isTokenExpired = !!selectedToken && selectedToken.validTo < new Date();
        }

        return [
            {
                id: "revoke",
                text: Resources.Revoke,
                onActivate: this.openRevokeTokenDialog,
                iconProps: {
                    iconName: "Undo"
                },
                disabled: !selectedToken || !selectedToken.isValid
            },
            {
                id: "regenerate",
                text: Resources.Regenerate,
                onActivate: this.openRegenerateDialog,
                iconProps: {
                    iconName: "Refresh"
                },
                disabled: !selectedToken || !selectedToken.isValid || isTokenExpired || (!!selectedToken.targetAccounts?.length && hostPageData?.hostId !== selectedFilter.organization?.id)
            },
            {
                id: "edit",
                text: Resources.Edit,
                onActivate: this.openEditTokenDialog,
                iconProps: {
                    iconName: "Edit"
                },
                disabled: !selectedToken || !selectedToken.isValid || (!!selectedToken.targetAccounts?.length && hostPageData?.hostId !== selectedFilter.organization?.id)

            },
        ];
    }

    private hideRegenerateButtonToExpiredPersonalAccessTokens(): boolean {
        return isFeatureFlagEnabled(this.props.pageContext, "VisualStudio.Services.TenantPolicy.HideRegenerateButtonToExpiredPersonalAccessTokens", false);
    }

    private openRevokeTokenDialog = (): void => {
        this.props.panelAction.openTokenPanel(PanelOperationType.ConfirmRevoke, this.props.tokenContext.selectedToken);
        this.props.tokenContext.onSelectedTokenChange(undefined);
    }

    private openEditTokenDialog = (): void => {
        this.props.panelAction.openTokenPanel(PanelOperationType.Edit, this.props.tokenContext.selectedToken);
        this.props.tokenContext.onSelectedTokenChange(undefined);
    }

    private openRegenerateDialog = (): void => {
        this.props.panelAction.openTokenPanel(PanelOperationType.ConfirmRegenerate, this.props.tokenContext.selectedToken);
        this.props.tokenContext.onSelectedTokenChange(undefined);
    }
}

export const NewTokenList = (props: ITokenListProps) => (
    <PatPageConsumer>
        {(patPageContext: IPatPageContextValue) => {
            return (
                <TokenConsumer>
                    {(tokenContext: ITokenContextValue) => {
                        return (
                            <AccountNameConsumer>
                                {(accountNameContext: IAccountNameContextValue) => {
                                    return (
                                        <TokenListInternal
                                            {...props}
                                            tokenContext={tokenContext}
                                            accountNameContext={accountNameContext}
                                            panelAction={PanelActionCreator.getInstance()}
                                            tokenAction={TokenActionCreator.getInstance(patPageContext.pageContext)}
                                            tokenConfig={TokenConfig.getInstance(patPageContext.pageContext)}
                                            pageContext={patPageContext.pageContext}
                                        />
                                    );
                                }}
                            </AccountNameConsumer>
                        );
                    }}
                </TokenConsumer>
            );
        }}
    </PatPageConsumer>
)
